import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Brain, Calendar, ShieldCheck, ChevronRight, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import PackagesSection from "../components/PackagesSection";
import { API } from "../context/AuthContext";

const IMG = (p) => `${API}/static/illustrations/${p}`;

const Benefit = ({ icon: Icon, title, text }) => (
  <div className="flex gap-4">
    <span className="shrink-0 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#F0FDF4] ring-1 ring-[#16A34A]/20">
      <Icon className="h-5 w-5 text-[#16A34A]" />
    </span>
    <div>
      <h3 className="font-display text-xl font-semibold text-[#0A2518] mb-1">{title}</h3>
      <p className="text-[#4B5563] leading-relaxed">{text}</p>
    </div>
  </div>
);

export default function ForParents() {
  const { t } = useTranslation();
  return (
    <div data-testid="parents-page">
      <section className="relative overflow-hidden py-20">
        <span className="geo-shape bg-[#16A34A]/20 h-72 w-72 -top-20 -right-20 drift-slow" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="pill"><Sparkles className="h-3.5 w-3.5" /> {t("forParents.pill")}</span>
            <h1 className="mt-4 font-display text-5xl sm:text-6xl font-black text-[#0A2518] tracking-tight leading-tight">{t("forParents.title")}</h1>
            <p className="mt-6 text-lg text-[#4B5563]">{t("forParents.intro")}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Button asChild className="rounded-full h-12 px-7 bg-[#16A34A] hover:bg-[#15803D] text-white" data-testid="parents-cta-register">
                <Link to="/register?role=parent">{t("forParents.ctaRegister")} <ChevronRight className="h-4 w-4 ml-1" /></Link>
              </Button>
              <Button asChild variant="outline" className="rounded-full h-12 px-7 border-[#16A34A] text-[#16A34A] hover:bg-[#F0FDF4]">
                <Link to="/login">{t("common.alreadyAccount")}</Link>
              </Button>
            </div>
          </div>
          <div className="relative">
            <div className="rounded-3xl overflow-hidden border border-[#E5EBE7] shadow-xl">
              <img src={IMG("parents_hero.png")} alt="" className="w-full h-[520px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#F3F7F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12">
          <div>
            <h2 className="font-display text-4xl font-bold text-[#0A2518]">{t("forParents.section2Title")}</h2>
            <p className="mt-4 text-[#4B5563] leading-relaxed max-w-md">{t("forParents.section2Text")}</p>
          </div>
          <div className="space-y-8">
            <Benefit icon={ShieldCheck} title={t("forParents.b1Title")} text={t("forParents.b1Text")} />
            <Benefit icon={Brain} title={t("forParents.b2Title")} text={t("forParents.b2Text")} />
            <Benefit icon={Calendar} title={t("forParents.b3Title")} text={t("forParents.b3Text")} />
            <Benefit icon={CheckCircle2} title={t("forParents.b4Title")} text={t("forParents.b4Text")} />
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-4xl font-bold text-[#0A2518]">{t("forParents.finalTitle")}</h2>
          <p className="mt-4 text-[#4B5563] max-w-2xl mx-auto">{t("forParents.finalText")}</p>
          <Button asChild className="mt-8 rounded-full h-14 px-8 bg-[#16A34A] hover:bg-[#15803D] text-white">
            <Link to="/register?role=parent">{t("forParents.finalCta")}</Link>
          </Button>
        </div>
      </section>

      <PackagesSection />
    </div>
  );
}
